import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';
import AdminLayout from '../../src/components/AdminLayout';
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger 
} from '../../components/ui/dropdown-menu';
import { ChevronDown, RefreshCw, Copy, Sparkles, Trash2 } from 'lucide-react';

export default function MissingQueries() {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortOrder, setSortOrder] = useState('count-desc');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchQueries();
  }, [sortOrder]);

  async function fetchQueries() {
    setLoading(true);

    let query = supabase.from('missing_queries').select('*');
    
    if (sortOrder === 'count-desc') {
      query = query.order('search_count', { ascending: false });
    } else if (sortOrder === 'count-asc') {
      query = query.order('search_count', { ascending: true });
    } else if (sortOrder === 'date-desc') {
      query = query.order('created_at', { ascending: false });
    }

    const { data, error } = await query;
    if (error) {
      console.error('Error fetching missing queries:', error);
    } else {
      setQueries(data || []);
    }
    setLoading(false);
  }

  async function handleDelete(id) {
    if (!confirm('Delete this query log?')) return;
    const { error } = await supabase.from('missing_queries').delete().eq('id', id);
    if (!error) fetchQueries();
  }

  async function handleClearAll() {
    if (!confirm('Are you sure you want to CLEAR ALL missing queries? This cannot be undone.')) return;
    // Delete all rows — Supabase requires a filter, so we use created_at is not null
    const { error } = await supabase.from('missing_queries').delete().not('id', 'is', null);
    if (!error) fetchQueries();
  }

  function copyAsJSON() {
    const dataStr = JSON.stringify(queries, null, 2);
    navigator.clipboard.writeText(dataStr).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const sortLabels = {
    'count-desc': 'Sort by Count (High to Low)',
    'count-asc': 'Sort by Count (Low to High)',
    'date-desc': 'Sort by Recent Date',
  };

  // Badge color based on count
  function countBadgeColor(count) {
    if (count >= 10) return 'bg-fuchsia-900/30 text-fuchsia-300 border-fuchsia-800/40';
    if (count >= 5) return 'bg-indigo-900/30 text-indigo-300 border-indigo-800/40';
    return 'bg-slate-800 text-slate-300 border-slate-700';
  }

  return (
    <AdminLayout>
      <div className="flex flex-col space-y-6">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-white">Missing Queries Monitor</h1>
            <p className="text-sm text-slate-400 mt-1">
              {loading ? 'Loading...' : `${queries.length} unique search terms logged`}
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center bg-[#1e293b] border border-slate-700 text-slate-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#334155] transition-colors outline-none">
                {sortLabels[sortOrder]}
                <ChevronDown className="w-4 h-4 ml-2" />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#1e293b] border-slate-700 text-white z-50">
                <DropdownMenuItem onClick={() => setSortOrder('count-desc')} className="hover:bg-slate-700 cursor-pointer">Count (High to Low)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortOrder('count-asc')} className="hover:bg-slate-700 cursor-pointer">Count (Low to High)</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortOrder('date-desc')} className="hover:bg-slate-700 cursor-pointer">Last Added Date</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button onClick={fetchQueries} className="flex items-center bg-[#1e293b] hover:bg-[#334155] border border-slate-700 text-slate-300 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
              <RefreshCw className={`w-4 h-4 mr-2 text-indigo-400 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>

            <button onClick={copyAsJSON} className="flex items-center bg-[#1e293b] hover:bg-[#334155] border border-slate-700 text-slate-300 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
              <Copy className="w-4 h-4 mr-2 text-orange-400" />
              {copied ? 'Copied!' : 'Copy as JSON'}
            </button>

            <button className="flex items-center bg-[#1e293b] hover:bg-[#334155] border border-slate-700 text-slate-300 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
              <Sparkles className="w-4 h-4 mr-2 text-fuchsia-400" />
              Ask AI
            </button>

            <button onClick={handleClearAll} className="flex items-center bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-400 px-4 py-2 rounded-lg text-sm font-medium transition-colors">
              <Trash2 className="w-4 h-4 mr-2" />
              Clear All
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-slate-800 bg-[#172033] overflow-hidden">
          <Table>
            <TableHeader className="bg-[#1a2336] border-b border-slate-800">
              <TableRow className="hover:bg-transparent border-none">
                <TableHead className="w-12 text-center pl-4">
                  <Checkbox className="border-slate-500" />
                </TableHead>
                <TableHead className="text-slate-400 font-semibold text-xs tracking-wider uppercase">Search Query</TableHead>
                <TableHead className="text-slate-400 font-semibold text-xs tracking-wider uppercase w-40">Times Searched</TableHead>
                <TableHead className="text-slate-400 font-semibold text-xs tracking-wider uppercase w-48">Date Added</TableHead>
                <TableHead className="text-right text-slate-400 font-semibold text-xs tracking-wider uppercase pr-6 w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12 text-slate-400">
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
                      <span>Loading queries...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : queries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-12">
                    <div className="flex flex-col items-center text-slate-400 space-y-2">
                      <span className="text-4xl">🎉</span>
                      <p className="font-medium text-slate-300">No missing queries logged yet!</p>
                      <p className="text-sm">When users search for games that aren't found, they'll appear here.</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                queries.map((q) => (
                  <TableRow key={q.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                    <TableCell className="text-center pl-4">
                      <Checkbox className="border-slate-500" />
                    </TableCell>

                    <TableCell>
                      <span className="font-semibold text-slate-200 text-base">
                        "{q.query_text}"
                      </span>
                    </TableCell>
                    
                    <TableCell>
                      <span className={`font-medium px-2.5 py-1 rounded-md text-sm border ${countBadgeColor(q.search_count)}`}>
                        {q.search_count} {q.search_count === 1 ? 'time' : 'times'}
                      </span>
                    </TableCell>
                    
                    <TableCell>
                      <div className="text-slate-300 flex flex-col">
                        <span className="text-sm">{new Date(q.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        <span className="text-xs text-slate-500">{new Date(q.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                    </TableCell>

                    <TableCell className="text-right pr-6">
                      <button 
                        onClick={() => handleDelete(q.id)} 
                        className="text-slate-500 hover:text-rose-400 transition-colors p-2 rounded-md hover:bg-slate-800"
                        title="Delete this query"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Summary row */}
        {!loading && queries.length > 0 && (
          <div className="flex items-center justify-between text-sm text-slate-500 py-1">
            <span>Total: <span className="text-white font-semibold">{queries.length}</span> unique search terms</span>
            <span>
              Total searches: <span className="text-white font-semibold">
                {queries.reduce((acc, q) => acc + (q.search_count || 0), 0)}
              </span>
            </span>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
